import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash';

import { LinkService } from '../link.service';
import { TimeCurrencyService } from '../../time-currency.service';
import { AppService } from '../../../app.service';

import { CONSTANT_VALUES } from '../../../constants/constant-values';
import { CONSTANT_MESSAGES } from '../../../constants/constants-messages';
import { ISlimScrollOptions } from 'ng2-slimscroll';

import {
  MessageEvent, SignInEvent, LoaderEvent, OpenFrameEvent,
  SidemenuSubCategory, HiddenFooter, SetAllMemberCheck
} from '../../../shared/message.event';

import { UISETTING } from '../../../shared/ui-setting';
import { UtilityFunctions } from '../../../shared/utility-functions';

@Component({
  selector: 'app-link-dashboard',
  templateUrl: './link-dashboard.component.html',
  styleUrls: ['./link-dashboard.component.css'],
  providers: [
    LinkService, MessageEvent, SignInEvent, LoaderEvent,
    OpenFrameEvent, SidemenuSubCategory, HiddenFooter, SetAllMemberCheck,
    TimeCurrencyService
  ]
})
export class LinkDashboardComponent implements OnInit {

  // services we access in template or methods
  public linkService: LinkService;
  public appService: AppService;
  public timeCurrencyService: TimeCurrencyService;
  public messageEvent: MessageEvent;
  public signInEvent: SignInEvent;
  public loaderEvent: LoaderEvent;
  public openFrameEvent: OpenFrameEvent;
  public sidemenuSubCategory: SidemenuSubCategory;
  public hiddenFooter: HiddenFooter;
  public setAllMemberCheck: SetAllMemberCheck;

  private _router: Router;

  // UI & state
  public shouldSlide = false;
  public clubName = '';
  public messageNoLinksAdded = '';
  public uiSetting: UISETTING;
  public height: string;
  public scrollOptions: ISlimScrollOptions;

  // vendor
  public vendor: any = null;

  // data
  public rawLinks: any[] = [];
  public activeLinks: any[] = [];
  public expiredLinks: any[] = [];
  public bgColor: string[] = CONSTANT_VALUES.COLOR || [];

  // tabs / filters
  public activeTab = 'active';
  public showActive = true;
  public showExpired = false;

  // computed flag for template
  public get hasAnyLinks(): boolean {
    return (this.activeLinks && this.activeLinks.length > 0) ||
           (this.expiredLinks && this.expiredLinks.length > 0);
  }

  constructor(
    router: Router,
    linkService: LinkService,
    appService: AppService,
    timeCurrencyService: TimeCurrencyService,
    messageEvent: MessageEvent,
    signInEvent: SignInEvent,
    loaderEvent: LoaderEvent,
    openFrameEvent: OpenFrameEvent,
    sidemenuSubCategory: SidemenuSubCategory,
    hiddenFooter: HiddenFooter,
    setAllMemberCheck: SetAllMemberCheck
  ) {
    // assign injected services to public fields used in template
    this._router = router;
    this.linkService = linkService;
    this.appService = appService;
    this.timeCurrencyService = timeCurrencyService;
    this.messageEvent = messageEvent;
    this.signInEvent = signInEvent;
    this.loaderEvent = loaderEvent;
    this.openFrameEvent = openFrameEvent;
    this.sidemenuSubCategory = sidemenuSubCategory;
    this.hiddenFooter = hiddenFooter;
    this.setAllMemberCheck = setAllMemberCheck;

    // UI sizing constants
    this.uiSetting = {
      windowHeight: window.innerHeight,
      topNav: 56,
      midNav: 45,
      footer: 35,
      container: 40
    };

    // slimScroll options (kept as you had them)
    this.scrollOptions = {
      barBackground: '#343a40',
      barOpacity: '0.6',
      position: 'right',
      barWidth: '6',
      barBorderRadius: '5',
      barMargin: '1px 1px',
      gridBackground: '#adb5bd',
      gridOpacity: '1',
      gridWidth: '4',
      gridBorderRadius: '10',
      gridMargin: '1px 2px'
    };

    this.height = (
      this.uiSetting.windowHeight -
      (this.uiSetting.topNav + this.uiSetting.midNav + this.uiSetting.container + this.uiSetting.footer)
    ) + 'px';

    // Reuse message from events, replace “Event” with “Link”
    const baseMsg = (CONSTANT_MESSAGES && CONSTANT_MESSAGES.MESSAGE_NO_EVENTS_ADDED) ? CONSTANT_MESSAGES.MESSAGE_NO_EVENTS_ADDED : '';
    this.messageNoLinksAdded = baseMsg.replace(/Event/gi, 'Link');
  }

  ngOnInit(): void {
  // Reset UI frame state
  this.emitOpenFrameEvent();
  window.scrollTo(0, 0);

  /* ---------------- Vendor ---------------- */
  try {
    const vendorStr = localStorage.getItem('vendor');
    this.vendor = vendorStr ? JSON.parse(atob(vendorStr)) : {};
  } catch (err) {
    console.warn('Failed to parse vendor from localStorage', err);
    this.vendor = {};
  }

  /* ---------------- Club Name ---------------- */
  if (this.vendor && this.vendor.role === CONSTANT_VALUES.SUPER_ADMIN) {
    if (Array.isArray(this.vendor.subVendors) && this.vendor.subVendors.length > 0) {
      const subVendor = this.vendor.subVendors[0];
      this.clubName = subVendor && subVendor.clubName ? subVendor.clubName : '';
    } else {
      this.clubName = (this.vendor && this.vendor.clubName) ? this.vendor.clubName : '';
    }
  } else {
    this.clubName = (this.vendor && this.vendor.clubName) ? this.vendor.clubName : '';
  }

  /* ---------------- Sidebar & Footer ---------------- */
  const obj: any = { category: 'All Links', menu: 'Links' };
  try {
    this.sidemenuSubCategory.fire(obj);
  } catch (err) {
    // ignore if broadcaster not ready
  }

  this.emitTypeBroadcast();
  this.registerTypeBroadcast();

  /* ---------------- Cleanup Old State ---------------- */
  this.removePreviousLinkFromLocalStorage();

  /* ---------------- Data Load ---------------- */
  this.getLinksByClub();
}

  /* ---------------- Data load & shaping ---------------- */

  getLinksByClub(): void {
  // determine vendorId to pass to backend
  const vendorObj: any = { vendorId: '', showPastData:true };

  if (this.vendor && this.vendor.role === CONSTANT_VALUES.ROLE_VENDOR_ADMIN) {
    // vendor admin -> return links for that vendor
    vendorObj.vendorId = this.vendor._id;
  } else if (this.vendor && this.vendor.role === CONSTANT_VALUES.SUPER_ADMIN) {
    // super admin -> use first subVendor if available
    if (this.vendor.subVendors && this.vendor.subVendors.length > 0) {
      const subVendor = this.vendor.subVendors[0];
      vendorObj.vendorId = subVendor._id;
    }
  }

  // run request
  this.loaderEvent.fire(false);

  this.linkService.getClubLinks(vendorObj)
    .then((result: any) => {
      if (result && result.status === 'SUCCESS') {
        let incoming: any[] = result.linksData || [];

        // Backend may return grouped structure [{ categoryName, data: [] }, ...]
        if (incoming.length > 0 && incoming[0] && incoming[0].data && typeof incoming[0].data.splice === 'function') {
          const flat: any[] = [];
          for (let i = 0; i < incoming.length; i++) {
            const grp = incoming[i];
            const arr = grp.data || [];
            for (let j = 0; j < arr.length; j++) {
              flat.push(arr[j]);
            }
          }
          this.rawLinks = flat;
        } else {
          // already flat
          this.rawLinks = incoming;
        }

        // decorate (icons, colors)
        this.decorateLinks();

        // separate into active / expired
        this.separateLinksByExpiry();

        // cache if possible
        try { localStorage.setItem('vendor_linksData', JSON.stringify(this.rawLinks)); } catch (e) { /* ignore */ }
      } else {
        // no data / error case -> clear lists
        this.rawLinks = [];
        this.activeLinks = [];
        this.expiredLinks = [];
      }

      this.loaderEvent.fire(true);
    })
    .catch((err: any) => {
      console.error('Error loading links:', err);
      this.rawLinks = [];
      this.activeLinks = [];
      this.expiredLinks = [];
      this.loaderEvent.fire(true);
    });
}


  private decorateLinks(): void {
    const colors = this.bgColor || [];
    let colorIdx = 0;

    for (let i = 0; i < this.rawLinks.length; i++) {
      const link = this.rawLinks[i] || {};

      // map icon class (defensive)
      const iconName = link.icon || '';
      const foundIcon: any = _.find(CONSTANT_VALUES.ICON_LIST_IMAGE || [], { name: iconName });
      link.iconClass = foundIcon ? foundIcon.iconClass : 'icon-default';

      // card color rotation
      link.color = (colors.length > 0) ? colors[colorIdx] : '#6c757d';
      colorIdx++;
      if (colorIdx >= colors.length) colorIdx = 0;
    }
  }

  private separateLinksByExpiry(): void {
    this.activeLinks = [];
    this.expiredLinks = [];

    const now = this.startOfDay(new Date());

    for (let i = 0; i < this.rawLinks.length; i++) {
      const link = this.rawLinks[i];

      // If no expiry date, treat as active
      if (!link || !link.expiryDate) {
        this.activeLinks.push(link);
        continue;
      }

      const exp = this.parseDateFlexible(link.expiryDate || '');
      if (isNaN(exp.getTime())) {
        // invalid date -> push into expired so it doesn't show in active
        this.expiredLinks.push(link);
        continue;
      }

      const expStart = this.startOfDay(exp);
      if (expStart < now) {
        this.expiredLinks.push(link);
      } else {
        this.activeLinks.push(link);
      }
    }

    // Sort lists
    const that = this;
    this.activeLinks.sort(function (a: any, b: any) {
      const da = that.parseDateFlexible(a && a.expiryDate ? a.expiryDate : '');
      const db = that.parseDateFlexible(b && b.expiryDate ? b.expiryDate : '');
      return da.getTime() - db.getTime();
    });

    this.expiredLinks.sort(function (a: any, b: any) {
      const da = that.parseDateFlexible(a && a.expiryDate ? a.expiryDate : '');
      const db = that.parseDateFlexible(b && b.expiryDate ? b.expiryDate : '');
      return db.getTime() - da.getTime();
    });
  }

  /* ---------------- Tabs / filters ---------------- */

  switchTab(tab: string): void {
    this.activeTab = tab;
    this.showActive = tab === 'active';
    this.showExpired = tab === 'expired';
  }

  getLinksToDisplay(): any[] {
    return this.showActive ? (this.activeLinks || []) : (this.expiredLinks || []);
  }

  /* ---------------- Navigation ---------------- */

  openAddLinkForm(): void {
    this._router.navigate(['/addlink']);
  }

  openBroadcastLinkForm(): void {
    this._router.navigate(['/link-broadcast']);
  }

  viewLink(link: any): void {
  // Save the selected link to localStorage so AddLink can pick it up
  try {
    localStorage.setItem('facility', JSON.stringify(link));
  } catch (e) {
    console.warn('Failed to save link to localStorage', e);
  }

  // Navigate to AddLink (edit mode will be determined there)
  this._router.navigate(['/addlink']);
}


  homeScreen(): void {
    const util = new UtilityFunctions();
    util.homeScreen(this);
  }

  /* ---------------- Shared UI bus (like Events) ---------------- */

  registerTypeBroadcast(): void {
    const that = this;
    this.messageEvent.on().subscribe(function (message: any) {
      that.shouldSlide = message;
    });
  }

  emitTypeBroadcast(): void {
    const isShow: any = {
      privacypolicy: true,
      support: false,
      footer: 'block'
    };
    try { this.signInEvent.fire('block'); } catch (e) { /* ignore */ }
    try { this.hiddenFooter.fire(isShow); } catch (e) { /* ignore */ }
  }

  emitOpenFrameEvent(): void {
    try { this.openFrameEvent.fire(false); } catch (e) { /* ignore */ }
  }

  removePreviousLinkFromLocalStorage(): void {
    try {
      if (localStorage.getItem('facility')) localStorage.removeItem('facility');
      if (localStorage.getItem('currentCategory')) localStorage.removeItem('currentCategory');
    } catch (e) { /* ignore */ }
  }

  /* ---------------- Formatting helpers ---------------- */

  formatEventDate(dateString: string): string {
    if (!dateString) return 'Invalid Date';
    const d = this.parseDateFlexible(dateString);
    if (isNaN(d.getTime())) return 'Invalid Date';
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  private parseDateFlexible(s: string): Date {
    if (!s) return new Date(NaN);
    const str = (s + '').trim();

    // yyyy-mm-dd
    let m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const y = Number(m[1]);
      const mm = Number(m[2]);
      const dd = Number(m[3]);
      const d = new Date(y, mm - 1, dd);
      if (d.getFullYear() === y && d.getMonth() === (mm - 1) && d.getDate() === dd) return d;
      return new Date(NaN);
    }

    // dd/mm/yyyy
    m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      const dd2 = Number(m[1]);
      const mm2 = Number(m[2]);
      const y2 = Number(m[3]);
      const d2 = new Date(y2, mm2 - 1, dd2);
      if (d2.getFullYear() === y2 && d2.getMonth() === (mm2 - 1) && d2.getDate() === dd2) return d2;
      return new Date(NaN);
    }

    // fallback
    const d3 = new Date(str);
    return d3;
  }

  private startOfDay(d: Date): Date {
    const t = new Date(d.getTime());
    t.setHours(0, 0, 0, 0);
    return t;
  }
}



// WEBPACK FOOTER //
// ./src/app/facility/link/link-dashboard/link-dashboard.component.ts